/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import mixpanel from 'mixpanel-browser';

import {
  AnalyticsApi,
  AnalyticsEvent,
  BackstageUserIdentity,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { Config } from '@backstage/config';


type MixpanelClientOptions = {
  identityApi: IdentityApi;
  discoveryApi: DiscoveryApi;
  clientConfig?: MixpanelClienteConfig;
}

type MixpanelClienteConfig = {
  projectToken?: string,
  debug?: boolean,
  ignoreDNT?: boolean,
  apiHost?: string,
  apiProxyPath?: string,
}


export class Mixpanel implements AnalyticsApi {
  private readonly userRef: Promise<BackstageUserIdentity>;
  private readonly projectToken: string;
  private readonly debug: boolean;
  private readonly ignoreDNT: boolean;
  private initialized: boolean = false;

  private constructor(options: MixpanelClientOptions) {
    const identityApi = options.identityApi;
    const discoveryApi = options.discoveryApi;

    const apiProxyPath = options.clientConfig?.apiProxyPath;

    this.userRef = this.getUserRef(identityApi);
    this.projectToken = options.clientConfig?.projectToken || '';
    this.debug = options.clientConfig?.debug  || false;
    this.ignoreDNT = options.clientConfig?.ignoreDNT || true;

    if (apiProxyPath) {
      try {
        discoveryApi.getBaseUrl(apiProxyPath).then(
        (mixpanelApiHost) => {
          this.initialize(mixpanelApiHost);
        })
      } catch(err) {
        console.log("Unable to get mixpanel proxy URL: " + err);
      }
    } else {
      const configApiHost = options.clientConfig?.apiHost;
      const mixpanelApiHost = configApiHost || 'https://api.mixpanel.com';
      this.initialize(mixpanelApiHost);
    }
    this.userRef.then(this.configure);
  }

  private initialize(mixpanelApiHost: string) {
    mixpanel.init(
      this.projectToken,
      {
        debug: this.debug,
        ignore_dnt: this.ignoreDNT,
        api_host: mixpanelApiHost,
      });
    this.initialized = true;
    mixpanel.track('sign up');
  }

  private configure(userRef: BackstageUserIdentity) {
      const githubUserParts = userRef.userEntityRef.split('/');
      if ( githubUserParts.length > 1 ) {
        mixpanel.register({ "githubUser": githubUserParts[1] });
      } else {
        mixpanel.register({ "githubUser": githubUserParts });
      }
      mixpanel.track('user configured');
  }

  static fromConfig(config: Config, options: MixpanelClientOptions) {
    const projectToken = config.getString('app.analytics.mixpanel.projectToken');
    const apiProxyPath = config.getOptionalString('app.analytics.mixpanel.apiProxyPath');
    const debug = config.getOptionalBoolean('app.analytics.mixpanel.debug');
    const ignoreDNT = config.getOptionalBoolean('app.analytics.mixpanel.ignoreDNT');
    const apiHost = config.getOptionalString('app.analytics.mixpanel.apiHost');
    const discoveryApi = options.discoveryApi;
    const identityApi = options.identityApi;

    return new Mixpanel({
      identityApi,
      discoveryApi,
      clientConfig: {
        projectToken,
        debug,
        ignoreDNT,
        apiHost,
        apiProxyPath,
      }
    });
  }

  private async getUserRef(identityApi: IdentityApi): Promise<BackstageUserIdentity> {
    return await identityApi.getBackstageIdentity();
  }

  captureEvent(event: AnalyticsEvent) {
    if (this.initialized) {
      const { action, ...rest } = event;
      mixpanel.track(action, rest);
    }
  }
}
